import React, {useState} from 'react';
import PropTypes from "prop-types";
import {DateFormatter, DateParser} from "../../../../services/date"

import RequestDetailsSection from "./RequestDetailsSection";

import {
    Accordion,
    AccordionDetails,
    AccordionActions,
    AccordionSummary,
    Box,
    Grid,
    Typography,
    styled
} from "@mui/material";

import {green, red} from "@mui/material/colors";

import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import LoadingButton from "@mui/lab/LoadingButton";


const StyledPersonIcon = styled(PersonIcon)({
    marginLeft: "-3px",
    marginRight: "-3px",
})

const StyledArrivalIcon= styled(ArrowRightIcon)({
    margin: "-8px -4px -8px -8px",
    color: green[500]
})

const StyledDepartureIcon= styled(ArrowLeftIcon)({
    margin: "-8px -4px -8px -8px",
    color: red[500]
})

const Request = props => {
    const {request, isExpanded, onExpand, onRequestResend} = props;
    const [isResending, setIsResending] = useState(false);

    const {
        id,
        incrementalNumber,
        name,
        surname,
        arrival,
        departure,
        adults,
        children,
        childrenAge,
        petsNumber,
        content,
        nationality,
        language,
        sendTime,
        numberOfSend,
    } = request;

    const from = DateParser.safeParseISO(arrival);
    const to = DateParser.safeParseISO(departure);
    const createdAt = DateParser.safeParseISO(sendTime);

    const requestsDetails = [
        {
            sectionName: "Anagrafica ospite",
            fields: [
                {
                    name: "Nome",
                    value: name,
                    visible: true
                },
                {
                    name: "Cognome",
                    value: surname,
                    visible: true
                },
                {
                    name: "Nazionalità",
                    value: nationality,
                    visible: !!nationality
                },
                {
                    name: "Lingua parlata",
                    value: language,
                    visible: !!language
                }
            ]
        },
        {
            sectionName: "Soggiorno richiesto",
            fields: [
                {
                    name: "Data arrivo",
                    value: DateFormatter.longString(from),
                    visible: true
                },
                {
                    name: "Data partenza",
                    value: DateFormatter.longString(to),
                    visible: true
                },
                {
                    name: "Numero adulti",
                    value: adults,
                    visible: true
                },
                {
                    name: "Numbero bambini",
                    value: children,
                    visible: true
                },
                {
                    name: "Età bambini",
                    value: childrenAge,
                    visible: !!childrenAge
                },
                {
                    name: "Animali",
                    value: petsNumber,
                    visible: true
                },
                {
                    name: "Messaggio",
                    value: content,
                    visible: !!content
                },

            ]
        }
    ];

    const handleResend = async () => {
        setIsResending(true);
        await onRequestResend(id);
        setIsResending(false);
    };

    const getPeopleIcons = () => {
        const people = adults + children;
        if (people > 4) {
            return (
                <React.Fragment>
                    <Box sx={{
                        height: "24px",
                        width: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        alignContent: "center"
                    }}>
                        <Box sx={{fontWeight: 500}}>
                            {people}
                        </Box>
                    </Box>
                    <StyledPersonIcon/>
                </React.Fragment>
            )
        } else {
            return (
                <React.Fragment>
                    {[...Array(people)].map((_, i) => <StyledPersonIcon key={i}/>)}
                </React.Fragment>
            )
        }
    };

    return (
        <Accordion expanded={isExpanded} onChange={onExpand}>

            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                aria-controls={`Richiesta-${id}`}
                id={`filtered-request-${id}`}>

                <Box sx={{flexGrow: 1, display: "flex", flexDirection: "row"}}>
                    <Box sx={{
                             flexShrink: 0, // NOT shrink the component
                             flexBasis: {xs: "36px", md: "72px"},
                             display: "flex",
                             flexWrap: "wrap",
                             alignItems: "center",
                             justifyContent: "center",
                             alignContent: "center"
                         }}>
                        {getPeopleIcons()}
                    </Box>

                    <Box sx={{px: 2, flexGrow: 1}}>
                        <Grid container>
                            <Grid item xs={12} md={4} xl={3}>
                                <Typography variant="subtitle1">
                                    <Box sx={{pr: 1, fontWeight: 500}}>
                                        {`${name} ${surname}`}
                                    </Box>
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6} xl={8}>
                                <Typography component="div" variant="subtitle1" color="textSecondary">
                                    <Box sx={{display: "flex", flexWrap: "wrap"}}>
                                        <Box sx={{
                                            pr: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            alignContent: "center"
                                        }}>
                                            <StyledArrivalIcon/>
                                            {DateFormatter.longString(from)}
                                        </Box>
                                        <Box sx={{
                                            pr: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            alignContent: "center"
                                        }}>
                                            <StyledDepartureIcon/>
                                            {DateFormatter.longString(to)}
                                        </Box>
                                    </Box>
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={2} xl={1}>
                                <Box sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row-reverse"
                                }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {DateFormatter.shortString(createdAt)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Box sx={{width: "100%"}}>
                    <Box sx={{pb: 3}}>
                        <Typography variant="h6" color="primary">
                            Richiesta n° {incrementalNumber} del {DateFormatter.onlyYearString(createdAt)}
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {requestsDetails.map((x, i) => <RequestDetailsSection key={i}
                                                                              name={x.sectionName}
                                                                              fields={x.fields}/>)}
                    </Grid>
                </Box>
            </AccordionDetails>


            <AccordionActions>
                <Box sx={{mr: 1, mb: 1}}>
                    {numberOfSend < 3 ?
                        <LoadingButton size="small"
                                       color="primary"
                                       variant="contained"
                                       loading={isResending}
                                       onClick={handleResend}>
                            Inviami questa richiesta
                        </LoadingButton>
                        :
                        <Typography variant={"body2"} color="error">
                            Raggiunto il massimo numero di inoltri
                        </Typography>
                    }
                </Box>
            </AccordionActions>

        </Accordion>
    )
};

Request.propTypes = {
    request: PropTypes.object.isRequired,
    isExpanded: PropTypes.bool.isRequired,
    onExpand: PropTypes.func,
    onRequestResend: PropTypes.func.isRequired,
};

export default Request;